import clsx from 'clsx';
import { isEqual } from 'lodash';
import { createPortal } from 'react-dom';
import { useEffect, useState, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { arrayMove, horizontalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  defaultDropAnimationSideEffects,
  type UniqueIdentifier,
  type DragOverEvent,
  type DragEndEvent,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  DragOverlay,
  DndContext,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import { useTags } from '@/hooks/rxdb-models';
import { useHiddenElementsCount } from '@flintmint/web-shared/hooks/use-hidden-elements-count';

import { TagChip } from '@/components/tag-chip';
import { SortableList } from '@/components/sortable-list';
import { Icon } from '@flintmint/web-shared/components/icon';
import { OverflowingTags } from '@/components/overflowing-tags';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { TagDropdown } from '@/components/tag-dropdown/tag-dropdown';

import { getGridInsertionIndex } from '@/utils/helper-utils';
import { animateOpacity } from '@flintmint/web-shared/constants/ui';

import { type Tag } from '@flintmint/core-shared/rxdb';

import styles from './tags-set.module.scss';

const EMPTY_TAGS: Tag[] = [];

export const TagsSet = (props: TagsSetProps) => {
  const { tagIds = [], sortable = true, prefix, appearance = 'default', onChange, onClear, overflowing = true, disabled } = props;

  const { data: tags } = useTags({ ids: tagIds });

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | undefined>(undefined);

  const [items, setItems] = useState<Record<string, string[]>>({
    root: tagIds,
    overflow: [],
  });

  const itemsUpdatedByDrag = useRef(false);
  const lastVisibilityUpdate = useRef<{ tagIds: string; visibleCount: number | null }>({
    tagIds: '',
    visibleCount: null,
  });
  const [originalItemsOnDragStart, setOriginalItemsOnDragStart] = useState<Record<string, string[]> | null>(null);

  const stableTags = tags.length ? tags : EMPTY_TAGS;
  const localTags: Tag[] = [...items.root, ...items.overflow].map((id) => stableTags.find((el) => el.id === id)!).filter(Boolean);
  const rootTags: Tag[] = items.root.map((id) => stableTags.find((el) => el.id === id)!).filter(Boolean);
  const overflowTags: Tag[] = items.overflow.map((id) => stableTags.find((el) => el.id === id)!).filter(Boolean);

  const { ref, ready, visibleCount, hiddenCount } = useHiddenElementsCount(stableTags, overflowing);

  const tagIdsString = useMemo(() => JSON.stringify(tagIds), [tagIds]);

  useEffect(() => {
    if (!sortable) {
      setItems({
        root: tagIds,
        overflow: [],
      });
    }
  }, [tagIds, sortable]);

  useEffect(() => {
    if (itemsUpdatedByDrag.current) {
      return;
    }

    if (sortable && visibleCount !== null && tagIds.length > 0 && !activeId) {
      const newRoot = tagIds.slice(0, visibleCount);
      const newOverflow = tagIds.slice(visibleCount);

      if (!isEqual(items.root, newRoot) || !isEqual(items.overflow, newOverflow)) {
        lastVisibilityUpdate.current = {
          tagIds: tagIdsString,
          visibleCount,
        };
        setItems({
          root: newRoot,
          overflow: newOverflow,
        });
      }
    }
  }, [sortable, tagIds, tagIdsString, visibleCount, activeId, items.root, items.overflow]);

  useEffect(() => {
    if (!sortable || activeId || visibleCount === null) {
      return;
    }

    if (visibleCount >= tagIds.length && items.overflow.length > 0) {
      lastVisibilityUpdate.current = {
        tagIds: tagIdsString,
        visibleCount,
      };
      itemsUpdatedByDrag.current = false;
      setItems({
        root: tagIds,
        overflow: [],
      });
    }
  }, [sortable, activeId, visibleCount, tagIds, tagIdsString, items.overflow.length]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const findContainer = (id: string) => {
    if (id === 'overflow-tags') {
      return 'overflow';
    }

    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) => items[key].includes(id as string));
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!active || !over) {
      return setOverId(undefined);
    }

    setOverId(findContainer(over.id as string));

    const id = active.id as string;
    const overId = over.id as string;

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    requestAnimationFrame(() => {
      setItems((prev) => {
        const activeItems = prev[activeContainer];
        const overItems = prev[overContainer];

        const activeIndex = activeItems.indexOf(id);

        const newIndex = getGridInsertionIndex({
          activeId: id,
          overId,
          overContainer,
          prev,
          overRect: over.rect,
        });

        const movingItem = activeItems[activeIndex];
        const alreadyThere = overItems[newIndex] === movingItem || (newIndex > 0 && overItems[newIndex - 1] === movingItem);

        if (alreadyThere) {
          return prev;
        }

        itemsUpdatedByDrag.current = true;
        return {
          ...prev,
          [activeContainer]: prev[activeContainer].filter((it: string) => it !== id),
          [overContainer]: [...overItems.slice(0, newIndex), movingItem, ...overItems.slice(newIndex)],
        };
      });
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    setOverId(undefined);
    lastVisibilityUpdate.current = { tagIds: '', visibleCount: null };
    setOriginalItemsOnDragStart(null);

    if (!active || !over) return;

    const id = active.id as string;
    const overId = over.id as string;

    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer) {
      return;
    }

    if (activeContainer !== overContainer) {
      const isInTargetContainer = items[overContainer].includes(id);

      if (!isInTargetContainer) {
        itemsUpdatedByDrag.current = true;

        return setItems((curr) => {
          const activeItems = curr[activeContainer];
          const overItems = curr[overContainer];
          const activeIndex = activeItems.indexOf(id);

          if (activeIndex < 0) return curr;

          const movingItem = activeItems[activeIndex];

          return {
            ...curr,
            [activeContainer]: activeItems.filter((it: string) => it !== id),
            [overContainer]: [...overItems, movingItem],
          };
        });
      }
    }

    if (overId in items) {
      // Dropped on container itself, append to end if different position
      const activeIndex = items[activeContainer].indexOf(id);
      const lastIndex = items[overContainer].length - 1;

      if (activeIndex !== lastIndex) {
        itemsUpdatedByDrag.current = true;
        setItems((curr) => ({
          ...curr,
          [overContainer]: arrayMove(curr[overContainer], activeIndex, lastIndex),
        }));
      }
      return;
    }

    const activeIndex = items[activeContainer].indexOf(id);
    const overIndex = items[overContainer].indexOf(overId);

    if (overIndex >= 0 && activeIndex !== overIndex) {
      itemsUpdatedByDrag.current = true;
      setItems((curr) => ({
        ...curr,
        [overContainer]: arrayMove(curr[overContainer], activeIndex, overIndex),
      }));
    }
  };

  useEffect(() => {
    const newOrder = [...new Set([items.root, items.overflow].flat())];
    const canUpdate = !activeId && tagIds.length && newOrder.length && newOrder.length === tagIds.length;

    const isFromVisibilityUpdate =
      lastVisibilityUpdate.current.tagIds === tagIdsString && lastVisibilityUpdate.current.visibleCount === visibleCount;

    if (sortable && canUpdate && !isEqual(tagIds, newOrder) && !isFromVisibilityUpdate) {
      onChange(newOrder);
      itemsUpdatedByDrag.current = false;
    } else if (itemsUpdatedByDrag.current && isEqual(tagIds, newOrder)) {
      itemsUpdatedByDrag.current = false;
    }
  }, [sortable, items, tagIds, activeId, onChange, tagIdsString, visibleCount]);

  const renderOverflowCountAndNew = (
    <>
      {!!hiddenCount && overflowing && (
        <OverflowingTags
          tags={overflowTags}
          isDragging={!!activeId}
          activeId={activeId}
          appearance="greyscale"
          overId={overId}
          overflowCount={hiddenCount}
          onDelete={(tag) => onChange(tagIds.filter((tagId) => tagId !== tag.id))}
        />
      )}
      <TagDropdown
        onClose={onChange}
        attachedIds={tagIds}
        trigger={
          <span className={styles.addTagSecondaryBtn} data-testid="add-tags-btn-secondary">
            <Tooltip content="Add More Tags">
              <Icon size={18} multiColor type="plus-sign-square-bulk" />
            </Tooltip>
          </span>
        }
      />
    </>
  );

  // Read-only: no dropdown, no delete, no drag — nothing that can call onChange.
  const renderReadOnly = () =>
    localTags.length ? (
      <div className={clsx(styles.tagSet, styles[`a-${appearance}`])}>
        <div className={clsx(styles.tagChipsList, styles.ready)}>
          {localTags.map((tag) => (
            <TagChip tag={tag} key={tag.id} />
          ))}
        </div>
      </div>
    ) : (
      <button disabled className={styles.addTagBtn} data-testid="add-tags-btn-primary">
        <Icon multiColor type="new-tag" size={18} />
        Add Tags
      </button>
    );

  return (
    <AnimatePresence>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragCancel={() => {
          setActiveId(null);
          setOverId(undefined);
          lastVisibilityUpdate.current = { tagIds: '', visibleCount: null };
          if (originalItemsOnDragStart) {
            setItems(originalItemsOnDragStart);
            setOriginalItemsOnDragStart(null);
          }
          itemsUpdatedByDrag.current = false;
        }}
        onDragStart={(event) => {
          setActiveId(event.active.id);
          setOriginalItemsOnDragStart({ ...items });
        }}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {disabled ? (
          renderReadOnly()
        ) : !localTags.length ? (
          <TagDropdown
            onClose={onChange}
            attachedIds={tagIds}
            trigger={
              <motion.button className={styles.addTagBtn} data-testid="add-tags-btn-primary">
                <Icon multiColor type="new-tag" size={18} />
                Add Tags
              </motion.button>
            }
          />
        ) : (
          <motion.div
            className={clsx(styles.tagSet, styles[`a-${appearance}`], {
              [styles.overflowing]: overflowing,
            })}
            {...animateOpacity}
          >
            <div
              className={clsx(styles.tagChipsList, {
                [styles.ready]: overflowing ? ready : true,
              })}
            >
              {!!prefix && <span className={styles.includesTags}>{prefix}</span>}
              {sortable ? (
                <SortableList
                  id="root"
                  ref={ref}
                  activeId={activeId}
                  items={localTags || []}
                  contextItems={rootTags || []}
                  className={clsx(styles.tagChipsList, {
                    [styles.ready]: overflowing ? ready : true,
                  })}
                  strategy={horizontalListSortingStrategy}
                  renderItem={(props: any) => {
                    const { value, index, dragging, sorting: _sorting, listeners: _listeners, dragOverlay, ...rest } = props;
                    const isInRoot = items.root.includes(value.id);
                    const isInOverflow = items.overflow.includes(value.id);
                    const isVisibleByCount = visibleCount === null || index < visibleCount;
                    const allTagsShouldFit = visibleCount !== null && visibleCount >= tagIds.length;
                    const shouldHideOverflowItem = isInOverflow && (activeId !== null || !allTagsShouldFit);
                    const shouldHide = !dragOverlay && (!isVisibleByCount || (!!activeId && !isInRoot) || shouldHideOverflowItem);
                    return (
                      <TagChip
                        draggable
                        tag={value}
                        dragging={dragging || activeId === value.id}
                        dragOverlay={dragOverlay}
                        hidden={shouldHide}
                        onDelete={() => onChange(tagIds.filter((tagId) => tagId !== value.id))}
                        {...rest}
                      />
                    );
                  }}
                />
              ) : (
                localTags.map((tag) => (
                  <TagChip tag={tag} key={tag.id} onDelete={() => onChange(tagIds.filter((tagId) => tagId !== tag.id))} />
                ))
              )}
              {!overflowing && renderOverflowCountAndNew}
              {!!onClear && (
                <Tooltip content="Remove All">
                  <button data-testid="clear-tags-btn" className={styles.clearBtn} onClick={onClear}>
                    <Icon size={10} type="cancel-01" />
                  </button>
                </Tooltip>
              )}
            </div>
            {overflowing && renderOverflowCountAndNew}
          </motion.div>
        )}
        {createPortal(
          <DragOverlay
            adjustScale={false}
            dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: {
                  active: {
                    opacity: '0.5',
                  },
                },
              }),
            }}
          >
            {activeId ? (
              <TagChip data-testid="drag-overlay" tag={(tags || []).find((t) => t.id === activeId)!} dragOverlay />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </AnimatePresence>
  );
};

export interface TagsSetProps {
  tagIds?: string[];
  sortable?: boolean;
  onClear?: () => void;
  prefix?: string;
  overflowing?: boolean;
  appearance?: 'default' | 'light';
  disabled?: boolean;
  onChange: (tagIds: string[]) => void;
}
